import type React from 'react';
import { useRef, useState } from 'react';
import { useClickAway } from 'react-use';

import { Input, Text } from '@zillow/constellation-10';

import {
  StyledAutocompleteContainer,
  StyledDropdown,
  StyledDropdownItem,
  StyledDropdownItemText,
} from './StyledComponents';

type AutocompleteItem = {
  name: string;
};

type Props = {
  className: string;
  disabled?: boolean;
  items?: Array<AutocompleteItem>;
  onChange: (s: string) => void;
  onSelect: (item: AutocompleteItem) => void;
  placeholder?: string;
  value?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
};
const Autocomplete = ({
  className,
  disabled = false,
  items = [],
  onChange,
  onSelect,
  placeholder = 'Search',
  value = '',
  inputRef,
}: Props) => {
  const ref = useRef(null);
  const [isFocused, toggleFocus] = useState(false);
  const [activeIndex, updateActiveIndex] = useState(-1);

  useClickAway(ref, () => toggleFocus(false));

  const handleFocus = () => {
    toggleFocus(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // need e.persist() so that the debounced change handler has reference to the event
    // https://reactjs.org/docs/events.html#event-pooling
    e.persist();
    onChange(e.target.value);
  };

  const handleSelect = (item: AutocompleteItem) => {
    onSelect(item);
    toggleFocus(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const keyCode = e.key;
    toggleFocus(true);

    if (keyCode === 'ArrowUp') {
      const upOneIndex = activeIndex === -1 ? items.length - 1 : activeIndex - 1;
      updateActiveIndex(upOneIndex);
      e.preventDefault();
    } else if (keyCode === 'ArrowDown') {
      const downOneIndex = activeIndex === items.length - 1 ? -1 : activeIndex + 1;
      updateActiveIndex(downOneIndex);
      e.preventDefault();
    } else if (keyCode === 'Escape') {
      toggleFocus(false);
    } else if (keyCode === 'Tab') {
      toggleFocus(false);
    } else if (keyCode === 'Enter') {
      e.preventDefault();
      // if no activeIndex, default to first selection
      const selectedItem = activeIndex === -1 ? items[0] : items[activeIndex];
      if (selectedItem) {
        handleSelect(selectedItem);

        if (e.target instanceof HTMLElement) {
          e.target.blur();
        }
      } else {
        // user did not select an option
      }
    }
  };

  const renderDropdownItem = ({ idx, item }: { idx: number; item: AutocompleteItem }) => (
    <StyledDropdownItem
      data-testid="dropdown-item"
      isActive={idx === activeIndex}
      key={idx}
      onClick={() => handleSelect(items[idx])}
      onMouseEnter={() => updateActiveIndex(idx)}
      onMouseMove={() => updateActiveIndex(idx)}
    >
      <StyledDropdownItemText key={idx}>
        <Text>{item.name}</Text>
      </StyledDropdownItemText>
    </StyledDropdownItem>
  );

  const renderDropdown = () => {
    if (!isFocused) {
      return null;
    }
    if (items.length) {
      return <StyledDropdown>{items.map((item, idx) => renderDropdownItem({ item, idx }))}</StyledDropdown>;
    }
    return null;
  };

  /**
   * Note about disabling Google Chrome's autocomplete:
   * autocomplete="off || false" prop on <input> should work, however Constellation's
   * Input does not pass `...rest` to the <input>. Luckily, you can disable
   * autocomplete for an entire form in the same way.
   * The 'data-' prefix used to embed custom attributes on HTML elements and fix typescript errors.
   */
  return (
    <StyledAutocompleteContainer className={className}>
      <form ref={ref} data-autocomplete="off">
        <Input
          data-testid="autocomplete"
          ref={inputRef}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
        />

        {renderDropdown()}
      </form>
    </StyledAutocompleteContainer>
  );
};

export default Autocomplete;
